import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { Toast } from "primereact/toast";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { useSelector } from "react-redux";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import CustomDropdown from "../../custom/customDropdown";
import { AuthContext } from "../../Header/auth - context";
import "./Fakture.css";

/* Datum se šalje kroz toUtcIso — bez korekcije offseta toISOString ume da
   pomeri datum za dan unazad. */
const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
};

const pocetakGodine = () => new Date(new Date().getFullYear(), 0, 1);

const iznos = (v) =>
    v === null || v === undefined || v === ""
        ? ""
        : Number(v).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* Na obrascu prazna ćelija stoji kao crtica, ne kao belina — tako se vidi da
   podatak NE POSTOJI za tu vrstu reda, a ne da je zaboravljen. */
const crtica = (v) => (v === null || v === undefined || v === "" ? "–" : v);

const AnalitickaKartica = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const toast = useRef(null);
    const company = useSelector((s) => s.company);

    const [sifarnici, setSifarnici] = useState({});
    const [datumOd, setDatumOd] = useState(pocetakGodine());
    const [datumDo, setDatumDo] = useState(new Date());
    const [valuta, setValuta] = useState(null);
    const [samoSaSaldom, setSamoSaSaldom] = useState(true);

    const [partneri, setPartneri] = useState([]);
    const [ucitava, setUcitava] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);

    /* Dva nivoa na istom ekranu: spisak partnera, pa kartica jednog od njih.
       Povratak je jedno dugme — bez novog ruta i bez gubitka filtera. */
    const [kartica, setKartica] = useState(null);   // { zaglavlje, redovi }
    const [ucitavaKarticu, setUcitavaKarticu] = useState(false);

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 4000 });

    const posalji = async (putanja, telo = {}) => {
        const r = await fetch(process.env.REACT_APP_BACKEND_URL + "/izvod/" + putanja, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
            body: JSON.stringify(telo),
        });
        const tekst = await r.text();
        const d = tekst ? JSON.parse(tekst) : {};
        if (!r.ok) throw new Error(d.message || t("common.greska"));
        return d;
    };

    useEffect(() => {
        posalji("KarticaInit").then(setSifarnici).catch(() => {});
        // eslint-disable-next-line
    }, []);

    const ucitajPregled = () => {
        setUcitava(true);
        posalji("KarticaPregled", {
            datumOd: toUtcIso(datumOd),
            datumDo: toUtcIso(datumDo),
            valuta,
            samoSaSaldom: samoSaSaldom ? 1 : 0,
        })
            .then((d) => setPartneri(d || []))
            .catch((e) => poruka("error", t("common.greska"), e.message))
            .finally(() => setUcitava(false));
    };

    useEffect(() => { ucitajPregled(); /* eslint-disable-next-line */ }, []);

    const otvoriKarticu = (red) => {
        if (!red?.id_klijent) return;
        setUcitavaKarticu(true);
        posalji("Kartica", {
            id_klijent: red.id_klijent,
            datumOd: toUtcIso(datumOd),
            datumDo: toUtcIso(datumDo),
            valuta,
        })
            .then(setKartica)
            .catch((e) => poruka("error", t("common.greska"), e.message))
            .finally(() => setUcitavaKarticu(false));
    };

    /* ── Excel: isti raspored kolona kao na štampi ── */
    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const zaglavlje = kartica?.zaglavlje;
        const worksheet = workbook.addWorksheet(zaglavlje ? "Kartica" : "Pregled");

        if (zaglavlje) {
            worksheet.addRow([`${t("kartica.naslovKartice")} ${zaglavlje.Partner || ""}`]).font = { bold: true, size: 13 };
            worksheet.addRow([`${t("kartica.period")} ${zaglavlje.PeriodOd || ""} — ${zaglavlje.PeriodDo || ""}`]);
            worksheet.addRow([]);
        }

        const headers = zaglavlje
            ? [t("kartica.brojFakture"), t("kartica.brojIzvoda"), t("kartica.ziroRacun"), t("kartica.datum"),
               t("kartica.duguje"), t("kartica.potrazuje"), t("kartica.saldo"), t("nova.napomena")]
            : [t("kartica.partner"), t("sefUlazne.pib"), t("kartica.duguje"), t("kartica.potrazuje"), t("kartica.saldo")];

        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFEFEF" } };
            cell.border = { top: { style: "medium" }, left: { style: "medium" }, bottom: { style: "medium" }, right: { style: "medium" } };
        });

        const redovi = zaglavlje
            ? (kartica.redovi || []).map((r) => [
                  r.TipReda === "POCETNO" ? t("kartica.pocetnoStanje") : (r.BrojFakture || "–"),
                  r.BrojIzvoda || "–", r.ZiroRacun || "–", r.Datum || "",
                  Number(r.Duguje || 0), Number(r.Potrazuje || 0), Number(r.Saldo || 0), r.Napomena || "",
              ])
            : (partneri || []).map((r) => [
                  r.Partner || "", r.PIB || "",
                  Number(r.Duguje || 0), Number(r.Potrazuje || 0), Number(r.Saldo || 0),
              ]);

        redovi.forEach((red) => {
            const row = worksheet.addRow(red);
            row.eachCell((cell) => {
                cell.border = { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } };
            });
        });

        if (zaglavlje) {
            const ukupno = worksheet.addRow([
                t("kartica.ukupno"), "", "", "",
                Number(zaglavlje.UkupnoDuguje || 0),
                Number(zaglavlje.UkupnoPotrazuje || 0),
                Number(zaglavlje.Saldo || 0), "",
            ]);
            ukupno.font = { bold: true };
        }

        worksheet.columns.forEach((col) => {
            let max = 10;
            col.eachCell((cell) => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });

        const dt = new Date().toLocaleString("sr-RS", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit", timeZone: "Europe/Belgrade",
        }).replace(/,/g, "");
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `${zaglavlje ? t("kartica.naslovKratko") + " - " + (zaglavlje.Partner || "") : t("kartica.pregledKratko")} - ${company?.companyName || ""} - ${dt}.xlsx`);
    };

    /* ── Štampa: otvara se u novom prozoru sa obrascem kakav je na papiru ── */
    const stampaj = () => {
        const z = kartica?.zaglavlje;
        if (!z) return;
        const redovi = (kartica.redovi || []).map((r) => `
            <tr class="${r.TipReda === "POCETNO" ? "pocetno" : ""}">
                <td>${r.TipReda === "POCETNO" ? t("kartica.pocetnoStanje") : (r.BrojFakture || "–")}</td>
                <td class="c">${r.BrojIzvoda || "–"}</td>
                <td class="c">${r.ZiroRacun || "–"}</td>
                <td class="c">${r.Datum || ""}</td>
                <td class="d">${iznos(r.Duguje)}</td>
                <td class="d">${iznos(r.Potrazuje)}</td>
                <td class="d">${iznos(r.Saldo)}</td>
                <td>${r.Napomena || ""}</td>
            </tr>`).join("");

        const html = `
<!doctype html><html lang="sr"><head><meta charset="utf-8">
<title>${t("kartica.naslovKratko")} — ${z.Partner || ""}</title>
<style>
  body { font-family: "Segoe UI", Arial, sans-serif; color:#111; margin:18mm 12mm; }
  h1 { font-size:15pt; margin:0 0 2px; }
  .period { font-size:11pt; margin:0 0 4px; }
  .firma { font-size:9pt; color:#555; margin:0 0 14px; }
  table { border-collapse:collapse; width:100%; font-size:9pt; }
  th, td { border:1px solid #333; padding:3px 5px; }
  th { background:#eee; font-weight:700; }
  td.d { text-align:right; font-variant-numeric:tabular-nums; white-space:nowrap; }
  td.c { text-align:center; }
  tr.pocetno td, tfoot td { font-weight:700; background:#f4f4f4; }
  tfoot td.d { font-size:10pt; }
  @media print { body { margin:10mm; } }
</style></head><body>
  <h1>${t("kartica.naslovKartice")} ${z.Partner || ""}</h1>
  <p class="period">${t("kartica.period")} ${z.PeriodOd || ""} ${t("kartica.do")} ${z.PeriodDo || ""}</p>
  <p class="firma">${company?.companyName || ""} &nbsp;·&nbsp; ${t("sefUlazne.pib")}: ${z.PIB || "–"}</p>
  <table>
    <thead><tr>
      <th>${t("kartica.brojFakture")}</th><th>${t("kartica.brojIzvoda")}</th><th>${t("kartica.ziroRacun")}</th>
      <th>${t("kartica.datum")}</th><th>${t("kartica.duguje")}</th><th>${t("kartica.potrazuje")}</th>
      <th>${t("kartica.saldo")}</th><th>${t("nova.napomena")}</th>
    </tr></thead>
    <tbody>${redovi}</tbody>
    <tfoot><tr>
      <td colspan="4">${t("kartica.ukupno")}</td>
      <td class="d">${iznos(z.UkupnoDuguje)}</td>
      <td class="d">${iznos(z.UkupnoPotrazuje)}</td>
      <td class="d">${iznos(z.Saldo)}</td><td></td>
    </tr></tfoot>
  </table>
</body></html>`;

        const w = window.open("", "_blank");
        if (!w) { poruka("warn", t("common.upozorenje"), t("kartica.popupBlokiran")); return; }
        w.document.write(html);
        w.document.close();
        w.focus();
        w.print();
    };

    /* Zbirovi za trake iznad spiska partnera. */
    const zbir = useMemo(() => {
        const d = (partneri || []).reduce((s, r) => s + Number(r.Duguje || 0), 0);
        const p = (partneri || []).reduce((s, r) => s + Number(r.Potrazuje || 0), 0);
        return { duguje: d, potrazuje: p, saldo: d - p };
    }, [partneri]);

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded
                    onClick={exportExcel} data-pr-tooltip="XLS" />
            </div>
            <input type="search" placeholder={t("nova.phPretraga")} className="p-inputtext p-component"
                onInput={(e) => setGlobalFilter(e.target.value)} />
        </div>
    );

    // ── KARTICA jednog partnera ─────────────────────────────────────────────
    if (kartica?.zaglavlje) {
        const z = kartica.zaglavlje;
        return (
            <div className="col-12">
                <Toast ref={toast} />
                <div className="faktureStranica">
                    <Card>
                        <div className="ak-kartica-vrh">
                            <Button type="button" icon="pi pi-arrow-left" label={t("kartica.nazad")}
                                className="p-button-text p-button-sm" onClick={() => setKartica(null)} />
                            <div className="ak-kartica-naslov">
                                <p className="ak-naslov">{t("kartica.naslovKartice")} {z.Partner}</p>
                                <p className="ak-podnaslov">
                                    {t("kartica.period")} {z.PeriodOd} {t("kartica.do")} {z.PeriodDo}
                                    {z.PIB ? ` · ${t("sefUlazne.pib")} ${z.PIB}` : ""}
                                </p>
                            </div>
                            <div className="ak-akcije">
                                <Button type="button" icon="pi pi-print" label={t("kartica.stampa")}
                                    className="p-button-sm p-button-outlined" onClick={stampaj} />
                                <Button type="button" icon="pi pi-file-excel" label="XLS"
                                    className="p-button-sm p-button-outlined" onClick={exportExcel} />
                            </div>
                        </div>

                        {/* Tri broja koja se traže prva — pre nego što se uđe u redove. */}
                        <div className="ak-zbirovi">
                            <div className="ak-zbir">
                                <span className="ak-zbir-labela">{t("kartica.duguje")}</span>
                                <span className="ak-zbir-iznos">{iznos(z.UkupnoDuguje)}</span>
                            </div>
                            <div className="ak-zbir">
                                <span className="ak-zbir-labela">{t("kartica.potrazuje")}</span>
                                <span className="ak-zbir-iznos">{iznos(z.UkupnoPotrazuje)}</span>
                            </div>
                            <div className={`ak-zbir ak-saldo ${Number(z.Saldo) > 0 ? "duguje" : Number(z.Saldo) < 0 ? "potrazuje" : ""}`}>
                                <span className="ak-zbir-labela">{t("kartica.saldo")}</span>
                                <span className="ak-zbir-iznos">{iznos(z.Saldo)}</span>
                                <span className="ak-zbir-opis">
                                    {Number(z.Saldo) > 0 ? t("kartica.saldoDuguje")
                                        : Number(z.Saldo) < 0 ? t("kartica.saldoPotrazuje") : t("kartica.saldoNula")}
                                </span>
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <DataTable value={kartica.redovi || []} loading={ucitavaKarticu}
                            paginator rows={50} rowsPerPageOptions={[50, 100, 200]}
                            scrollable scrollHeight="55vh" size="small" showGridlines
                            emptyMessage={t("common.noDataTable")}
                            resizableColumns columnResizeMode="expand"
                            className="turePregledTable ak-tabela stavke-tabela"
                            rowClassName={(r) => ({ "ak-red-pocetno": r.TipReda === "POCETNO" })}>
                            <Column header={t("kartica.brojFakture")} style={{ minWidth: "130px" }}
                                body={(r) => r.TipReda === "POCETNO"
                                    ? <strong>{t("kartica.pocetnoStanje")}</strong>
                                    : crtica(r.BrojFakture)} />
                            <Column header={t("kartica.brojIzvoda")} style={{ minWidth: "100px" }}
                                body={(r) => <span className="ak-sredina">{crtica(r.BrojIzvoda)}</span>} />
                            <Column header={t("kartica.ziroRacun")} style={{ minWidth: "160px" }}
                                body={(r) => <span className="ak-sredina">{crtica(r.ZiroRacun)}</span>} />
                            <Column field="Datum" header={t("kartica.datum")} style={{ minWidth: "100px" }} />
                            <Column header={t("kartica.duguje")} style={{ minWidth: "120px" }}
                                body={(r) => <span className="ak-iznos">{iznos(r.Duguje)}</span>} />
                            <Column header={t("kartica.potrazuje")} style={{ minWidth: "120px" }}
                                body={(r) => <span className="ak-iznos">{iznos(r.Potrazuje)}</span>} />
                            <Column header={t("kartica.saldo")} style={{ minWidth: "130px" }}
                                body={(r) => <strong className="ak-iznos">{iznos(r.Saldo)}</strong>} />
                            <Column field="Napomena" header={t("nova.napomena")} style={{ minWidth: "180px" }} />
                        </DataTable>

                        {/* Red UKUPNO stoji van tabele da ga paginacija ne odnese na
                            drugu stranu — na obrascu je uvek poslednji. */}
                        <div className="ak-ukupno">
                            <span className="ak-ukupno-labela">{t("kartica.ukupno")}</span>
                            <span className="ak-iznos">{iznos(z.UkupnoDuguje)}</span>
                            <span className="ak-iznos">{iznos(z.UkupnoPotrazuje)}</span>
                            <strong className="ak-iznos">{iznos(z.Saldo)}</strong>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // ── PREGLED po partnerima ───────────────────────────────────────────────
    return (
        <div className="col-12">
            <Toast ref={toast} />
            <div className="faktureStranica">
                <Tooltip target=".export-buttons>button" position="bottom" />
                <Card>
                    <div className="sef-ulazne-filter">
                        <div className="sef-filter-polje">
                            <label>{t("sefUlazne.periodOd")}:</label>
                            <Calendar value={datumOd} onChange={(e) => setDatumOd(e.value)} dateFormat="dd.mm.yy." showIcon />
                        </div>
                        <div className="sef-filter-polje">
                            <label>{t("sefUlazne.periodDo")}:</label>
                            <Calendar value={datumDo} onChange={(e) => setDatumDo(e.value)} dateFormat="dd.mm.yy." showIcon />
                        </div>
                        <div className="sef-filter-polje">
                            <label>{t("ulazne.valuta")}:</label>
                            <CustomDropdown value={valuta} optionLabel="label" showClear
                                options={sifarnici.ValuteDB || []}
                                placeholder={t("kartica.sveValute")}
                                onChange={(e) => setValuta(e.target.value)} />
                        </div>
                        <div className="ulazne-kvacica" style={{ alignSelf: "end", paddingBottom: "0.4rem" }}>
                            <Checkbox inputId="samoSaSaldom" checked={samoSaSaldom}
                                onChange={(e) => setSamoSaSaldom(e.checked)} />
                            <label htmlFor="samoSaSaldom">{t("kartica.samoSaSaldom")}</label>
                        </div>
                        <div className="sef-filter-dugmad">
                            <Button type="button" label={t("sefUlazne.ucitaj")} icon="pi pi-search"
                                className="p-button-sm" loading={ucitava} onClick={ucitajPregled} />
                        </div>
                    </div>

                    <div className="ak-zbirovi ak-zbirovi-pregled">
                        <div className="ak-zbir">
                            <span className="ak-zbir-labela">{t("kartica.ukupnoDuguje")}</span>
                            <span className="ak-zbir-iznos">{iznos(zbir.duguje)}</span>
                        </div>
                        <div className="ak-zbir">
                            <span className="ak-zbir-labela">{t("kartica.ukupnoPotrazuje")}</span>
                            <span className="ak-zbir-iznos">{iznos(zbir.potrazuje)}</span>
                        </div>
                        <div className={`ak-zbir ak-saldo ${zbir.saldo > 0 ? "duguje" : zbir.saldo < 0 ? "potrazuje" : ""}`}>
                            <span className="ak-zbir-labela">{t("kartica.saldo")}</span>
                            <span className="ak-zbir-iznos">{iznos(zbir.saldo)}</span>
                        </div>
                        <div className="ak-zbir ak-broj">
                            <span className="ak-zbir-labela">{t("kartica.brojPartnera")}</span>
                            <span className="ak-zbir-iznos">{partneri.length}</span>
                        </div>
                    </div>
                </Card>

                <Card>
                    <DataTable value={partneri || []} loading={ucitava}
                        paginator rows={25} rowsPerPageOptions={[25, 50, 100]}
                        globalFilter={globalFilter} header={tableHeader}
                        scrollable scrollHeight="50vh" size="small"
                        emptyMessage={t("common.noDataTable")}
                        resizableColumns columnResizeMode="expand"
                        className="turePregledTable stavke-tabela"
                        onRowDoubleClick={(e) => otvoriKarticu(e.data)}>
                        <Column header="" style={{ width: "110px" }}
                            body={(r) => (
                                <Button type="button" label={t("kartica.otvori")} icon="pi pi-eye"
                                    className="p-button-text p-button-sm" onClick={() => otvoriKarticu(r)} />
                            )} />
                        <Column field="Partner" header={t("kartica.partner")} style={{ minWidth: "220px" }} />
                        <Column field="PIB" header={t("sefUlazne.pib")} style={{ minWidth: "110px" }} />
                        <Column header={t("kartica.duguje")} style={{ minWidth: "130px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Duguje)}</span>} />
                        <Column header={t("kartica.potrazuje")} style={{ minWidth: "130px" }}
                            body={(r) => <span className="ak-iznos">{iznos(r.Potrazuje)}</span>} />
                        <Column header={t("kartica.saldo")} style={{ minWidth: "140px" }}
                            body={(r) => (
                                <strong className={`ak-iznos ${Number(r.Saldo) > 0 ? "ak-plus" : Number(r.Saldo) < 0 ? "ak-minus" : ""}`}>
                                    {iznos(r.Saldo)}
                                </strong>
                            )} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

export default AnalitickaKartica;
